/// <reference path="../../../node_modules/@types/google.maps/index.d.ts"/>

import { MAP_ID, useGoogleMapInitialized } from '@hwm/ui-lib';
import { environment } from '@root/environment';
import { getGoogleMapMarkerForSiteOrMast, getStreetviewOptions } from '@shared/components/Map/Map.utils';
import { styleConstants } from '@styles/constants';
import { useEffect, useState, useCallback } from 'react';

export const defaultMapProps = {
  center: {
    lat: 48.57,
    lng: 13.72,
  },
  zoom: 14,
};

export interface MapStreetViewSiteModel {
  lat: number;
  lng: number;
  isMast?: boolean;
}

interface MapProps {
  onLoad?: () => void;
}

export const useMapInitialized = ({ onLoad }: MapProps) => {
  const googleMapInitialized = useGoogleMapInitialized({
    apiKey: environment.apiKey,
    onLoad,
    center: defaultMapProps.center,
    zoom: defaultMapProps.zoom,
  });
  const { map, infoWindow } = googleMapInitialized;

  const [bounds, setBounds] = useState<google.maps.LatLngBounds>();
  const [streetViewEnabled, setStreetViewEnabled] = useState<boolean>(false);
  const [sitesForStreetView, setSitesForStreetView] = useState<MapStreetViewSiteModel[]>([]);
  const [, setStreetViewMarkers] = useState<google.maps.Marker[]>([]);

  // street view
  useEffect(() => {
    if (!map || !bounds) return;

    map.fitBounds(bounds);
  }, [map, bounds]);

  useEffect(() => {
    if (!map || !streetViewEnabled) {
      setSitesForStreetView(null);
      return;
    }

    setTimeout(() => {
      google.maps.event.trigger(map.getStreetView(), 'resize');
    }, 500);
  }, [map, streetViewEnabled]);

  useEffect(() => {
    if (!map) return;

    map.setOptions({
      mapId: MAP_ID,
      fullscreenControl: true,
      maxZoom: 18,
      streetViewControl: true,
      mapTypeControl: true,
    });

    const streetViewListener = google.maps.event.addListener(map.getStreetView(), 'visible_changed', () => {
      const streetview = map.getStreetView();

      streetview.setOptions(getStreetviewOptions());

      const streetViewVisibility = streetview.getVisible();

      setBounds(map.getBounds());

      setStreetViewEnabled(streetViewVisibility);
    });

    return () => {
      google.maps.event.removeListener(streetViewListener);
    };
  }, [map]);

  useEffect(() => {
    if (!map || !sitesForStreetView || !streetViewEnabled) {
      setStreetViewMarkers((markers) => {
        markers.forEach((m) => m.setMap(null));
        return [];
      });
      return;
    }

    const newStreetViewMarkers =
      sitesForStreetView?.map((data) => {
        const marker = getGoogleMapMarkerForSiteOrMast(
          map,
          data.lat,
          data.lng,
          data.isMast ? null : styleConstants.colors.primaryColor
        );

        const googleMarker = new google.maps.Marker(marker);

        return googleMarker;
      }) ?? [];

    setStreetViewMarkers((markers) => {
      markers.forEach((m) => m.setMap(null));
      return newStreetViewMarkers;
    });

    return () => {
      setStreetViewMarkers((markers) => {
        markers.forEach((m) => m.setMap(null));
        return [];
      });
    };
  }, [map, streetViewEnabled, sitesForStreetView]);

  const handleShowPopupDetail = useCallback(
    (htmlString: string, lat: number, lng: number) => {
      infoWindow?.close();

      if (!map || !htmlString) {
        return;
      }

      infoWindow?.setPosition({ lat, lng });
      infoWindow?.setContent(htmlString);
      infoWindow?.open(map ?? null);
    },
    [map, infoWindow]
  );

  return {
    ...googleMapInitialized,
    handleShowPopupDetail,
    streetViewEnabled,
    sitesForStreetView,
    setSitesForStreetView,
  };
};

export type initializedMap = ReturnType<typeof useMapInitialized>;
